declare const __RILL_RELEASE__: string;
export const release = typeof __RILL_RELEASE__ === 'string' ? __RILL_RELEASE__ : 'development';
export const publicAnalyticsPaths = new Set(['/docs/screen-recording', '/docs/cloud-runs', '/docs/review-and-share', '/docs/chatgpt', '/docs/troubleshooting', '/chrome', '/docs/chrome-extension', '/adminbar', '/freebuff', '/blog/test-freebuff-app-with-rill', '/tools/website-to-video', '/browser-check', '/', '/pricing', '/how-it-works', '/use-cases', '/docs', '/docs/cli', '/docs/install-skill', '/docs/agent-quickstart', '/docs/plans', '/roadmap', '/login']);
export const homepageExperiment = 'homepage-task-entry';

export function publicPageProperties(pathname: string, referrer: string) {
  if (!publicAnalyticsPaths.has(pathname)) return null;
  let domain = '$direct';
  try {
    const url = new URL(referrer);
    if (['http:', 'https:'].includes(url.protocol) && url.hostname !== 'userill.dev') domain = url.hostname;
  } catch { /* Direct navigation. */ }
  return { $current_url: `https://userill.dev${pathname}`, $pathname: pathname, $host: 'userill.dev', $referrer: domain === '$direct' ? '' : `https://${domain}/`, $referring_domain: domain };
}

export type ErrorSource = 'browser_unhandled' | 'browser_rejection' | 'react_render' | 'api_network' | 'api_server' | 'worker_request' | 'oauth_callback';
const errorTypes = new Set(['Error', 'TypeError', 'RangeError', 'ReferenceError', 'SyntaxError', 'URIError', 'EvalError', 'AggregateError']);
export type ReactErrorKind = 'uncaught' | 'caught' | 'recoverable';
export function safeErrorRoute(pathname: string): string {
  if (publicAnalyticsPaths.has(pathname)) return pathname;
  if (['/settings', '/credentials', '/billing', '/record', '/recording-requests', '/activity', '/privacy', '/terms', '/blog'].includes(pathname)) return pathname;
  for (const [prefix, template] of [['/recordings/', '/recordings/:recordingId'], ['/request/', '/request/:requestToken'], ['/s/', '/s/:slug'], ['/embed/', '/embed/:slug'], ['/blog/', '/blog/:slug']]) {
    if (pathname.startsWith(prefix) && pathname.slice(prefix.length) && !pathname.slice(prefix.length).includes('/')) return template;
  }
  return 'unknown';
}
export interface ErrorContext { route?: string; reactErrorKind?: ReactErrorKind }
// Preserve exception messages; keep route templates and owned stack locations.
export function safeException(error: unknown, source: ErrorSource, context: ErrorContext = {}) {
  const type = error instanceof Error && errorTypes.has(error.name) ? error.name : 'Error';
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : 'Unknown error';
  const frames: Array<{ platform: string; lang: string; function: string; filename: string; lineno: number; colno: number; in_app: boolean }> = [];
  if (error instanceof Error) {
    for (const line of (error.stack ?? '').split('\n').slice(1, 30)) {
      const match = line.match(/https:\/\/userill\.dev\/assets\/([a-zA-Z0-9_-]+\.js)(?:\?[^\s)]*)?:(\d+):(\d+)\)?$/);
      if (match) {
        frames.push({ platform: 'custom', lang: 'javascript', function: '<redacted>', filename: match[1], lineno: Number(match[2]), colno: Number(match[3]), in_app: true });
      }
    }
  }
  const route = safeErrorRoute(context.route ?? '');
  const kind = source === 'react_render' && ['uncaught', 'caught', 'recoverable'].includes(context.reactErrorKind ?? '') ? context.reactErrorKind : undefined;
  const handled = kind ? kind !== 'uncaught' : !['browser_unhandled', 'browser_rejection', 'react_render'].includes(source);
  return {
    error_route: route,
    ...(kind ? { react_error_kind: kind } : {}),
    $exception_list: [{ type, value: message, mechanism: { handled, synthetic: false }, ...(frames.length ? { stacktrace: { type: 'raw', frames } } : {}) }],
    $exception_fingerprint: `${source}:${kind ?? 'unspecified'}:${type}:${route}:${frames[0]?.filename ?? release}:${frames[0]?.lineno ?? 0}:${frames[0]?.colno ?? 0}`,
    error_source: source,
    release,
  };
}
